import { useSyncExternalStore } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../contexts/AuthContext'
import {
  HELP_SIDES,
  HELP_STALE_MS,
  flattenArticles,
  helpApi,
  helpKeys,
  helpQueryRetry,
  helpSideForPersona,
  isHelpList,
  type HelpArticleSummary,
  type HelpSection,
  type HelpSide,
} from '../../lib/help'

// ── Admin's "View as" ─────────────────────────────────────────────────────

const VIEW_AS_KEY = 'ch_help_view_as'
const listeners = new Set<() => void>()

function readViewAs(): HelpSide {
  try {
    const saved = sessionStorage.getItem(VIEW_AS_KEY)
    return HELP_SIDES.includes(saved as HelpSide) ? (saved as HelpSide) : 'shop'
  } catch {
    return 'shop'
  }
}

let viewAs: HelpSide = readViewAs()

export function setHelpViewAs(side: HelpSide): void {
  viewAs = side
  try {
    sessionStorage.setItem(VIEW_AS_KEY, side)
  } catch {
    // Blocked storage: the choice lasts until the page reloads.
  }
  listeners.forEach((listener) => listener())
}

/** For tests: back to the default (Shop). */
export function resetHelpViewAs(): void {
  viewAs = 'shop'
  try {
    sessionStorage.removeItem(VIEW_AS_KEY)
  } catch {
    // ignore
  }
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export interface HelpSideState {
  /** The side help is read as; null when this persona has none (no company role yet). */
  side: HelpSide | null
  /** The platform admin previews a side with the "View as" switch. */
  canViewAs: boolean
  setViewAs: (side: HelpSide) => void
}

/**
 * Merchant, Manager, Worker read shop help; Courier courier help; Partner
 * partner help. The Admin persona picks one (Shop by default) — the server lets
 * a platform admin pass any side.
 */
export function useHelpSide(): HelpSideState {
  const { activePersona, isPlatformAdmin } = useAuth()
  const adminViewAs = useSyncExternalStore(subscribe, () => viewAs, () => viewAs)
  const canViewAs = activePersona === 'Admin' && isPlatformAdmin
  const side = canViewAs ? adminViewAs : helpSideForPersona(activePersona)
  return { side, canViewAs, setViewAs: setHelpViewAs }
}

// ── The visible articles ──────────────────────────────────────────────────

export interface HelpArticlesState {
  side: HelpSide | null
  /** True once GET /api/help/articles answered for the side. */
  available: boolean
  /** Still asking the server. */
  loading: boolean
  /** The server answered 404: a service-core without the help centre. */
  notFound: boolean
  sections: HelpSection[]
  articles: HelpArticleSummary[]
}

/**
 * The articles the caller may read. A 404 (a service-core without help), a 403
 * or any other failure leaves help unavailable: the "?" button, the palette's
 * Help group and the dashboard nudge stay hidden, and the side menu's
 * Help & support falls back to the support page.
 */
export function useHelpArticles(): HelpArticlesState {
  const { side } = useHelpSide()
  const query = useQuery({
    queryKey: helpKeys.articles(side ?? 'shop'),
    queryFn: () => helpApi.articles(side as HelpSide),
    enabled: side !== null,
    staleTime: HELP_STALE_MS,
    retry: helpQueryRetry,
  })
  const available = side !== null && query.isSuccess && isHelpList(query.data)
  const status = (query.error as { response?: { status?: number } } | null)?.response?.status
  return {
    side,
    available,
    loading: side !== null && query.isPending,
    notFound: status === 404,
    sections: available ? query.data!.sections : [],
    articles: available ? flattenArticles(query.data) : [],
  }
}
